/**
 * DEVLOGS-TEMP — global error capture across DOM, React Native and Node.
 *
 * Every hook is capability-probed and individually guarded, so one adapter file
 * works on a browser, a Hermes VM and an Electron main process without a single
 * platform import.
 *
 * TWO RULES TAKEN VERBATIM FROM EXISTING CODE IN THIS REPO:
 *
 *  1. ErrorUtils is CHAINED, never replaced (artifacts/tv/lib/crash-reporting.ts).
 *     Replacing it kills the dev red box and clean production termination.
 *  2. Node handlers EMIT AND LET THE DEFAULT PROCEED — they never swallow
 *     (artifacts/desktop/src/crash-reporter.ts: "a main process that limps on in
 *     an unknown state is worse than one that restarts").
 */

import { LEVELS } from "../level.ts";
import { sanitizeUrl } from "../redact.ts";
import type { EmitFn } from "../types.ts";

type RNErrorHandler = (error: unknown, isFatal?: boolean) => void;

export interface ErrorCaptureScope {
  addEventListener?: (type: string, listener: (event: unknown) => void, options?: unknown) => void;
  removeEventListener?: (type: string, listener: (event: unknown) => void, options?: unknown) => void;
  ErrorUtils?: {
    getGlobalHandler?: () => RNErrorHandler | undefined;
    setGlobalHandler?: (handler: RNErrorHandler) => void;
  };
  process?: {
    on?: (event: string, listener: (...args: unknown[]) => void) => void;
    off?: (event: string, listener: (...args: unknown[]) => void) => void;
  };
}

export interface ErrorCaptureOptions {
  /** Called on a terminal signal so the queue gets one last chance. */
  onFlush?: () => void;
}

export function installErrorCapture(
  scope: ErrorCaptureScope,
  emit: EmitFn,
  options: ErrorCaptureOptions = {},
): () => void {
  const teardown: Array<() => void> = [];

  // --- DOM -----------------------------------------------------------------
  if (typeof scope.addEventListener === "function") {
    const onError = (event: unknown): void => {
      try {
        const e = event as {
          error?: unknown;
          message?: string;
          filename?: string;
          lineno?: number;
          colno?: number;
          target?: unknown;
        };
        const target = e.target as
          | { tagName?: string; src?: string; currentSrc?: string; id?: string }
          | undefined;
        // A resource error (a failed <audio src>) has a target element and no
        // `error`. This is very often the only signal a media load ever emits.
        if (target && typeof target.tagName === "string") {
          emit(
            LEVELS.error,
            "error",
            "resource",
            {
              tag: target.tagName,
              id: target.id,
              src: sanitizeUrl(target.currentSrc ?? target.src),
            },
            { msg: `resource load failed: ${target.tagName}` },
          );
          return;
        }
        emit(
          LEVELS.error,
          "error",
          "uncaught",
          { file: sanitizeUrl(e.filename), line: e.lineno, col: e.colno },
          { err: e.error ?? e.message, msg: e.message },
        );
      } catch {
        /* fire-and-forget */
      }
    };
    // Capture phase, so resource errors (which do not bubble) are seen.
    scope.addEventListener("error", onError, true);
    teardown.push(() => scope.removeEventListener?.("error", onError, true));

    const onRejection = (event: unknown): void => {
      try {
        const e = event as { reason?: unknown };
        emit(LEVELS.error, "error", "unhandledrejection", undefined, { err: e.reason });
      } catch {
        /* fire-and-forget */
      }
    };
    scope.addEventListener("unhandledrejection", onRejection);
    teardown.push(() => scope.removeEventListener?.("unhandledrejection", onRejection));

    const onCsp = (event: unknown): void => {
      try {
        const e = event as { blockedURI?: string; violatedDirective?: string };
        emit(
          LEVELS.warn,
          "error",
          "csp",
          {
            blocked: sanitizeUrl(e.blockedURI),
            directive: e.violatedDirective,
          },
          { msg: "csp violation" },
        );
      } catch {
        /* fire-and-forget */
      }
    };
    scope.addEventListener("securitypolicyviolation", onCsp);
    teardown.push(() => scope.removeEventListener?.("securitypolicyviolation", onCsp));
  }

  // --- React Native --------------------------------------------------------
  const errorUtils = scope.ErrorUtils;
  if (
    errorUtils &&
    typeof errorUtils.getGlobalHandler === "function" &&
    typeof errorUtils.setGlobalHandler === "function"
  ) {
    const previous = safeCall(() => errorUtils.getGlobalHandler?.(), undefined);
    const handler: RNErrorHandler = (error, isFatal) => {
      try {
        emit(isFatal ? LEVELS.fatal : LEVELS.error, "error", "uncaught", { fatal: !!isFatal }, {
          err: error,
        });
      } catch {
        /* fire-and-forget */
      }
      // CHAIN. Never replace.
      try {
        previous?.(error, isFatal);
      } catch {
        /* the previous handler owns its own failures */
      }
    };
    safeVoid(() => errorUtils.setGlobalHandler?.(handler));
    teardown.push(() => {
      safeVoid(() => {
        if (errorUtils.getGlobalHandler?.() === handler && previous) {
          errorUtils.setGlobalHandler?.(previous);
        }
      });
    });
  }

  // --- Node ----------------------------------------------------------------
  const proc = scope.process;
  if (proc && typeof proc.on === "function") {
    const onUncaught = (...args: unknown[]): void => {
      try {
        emit(LEVELS.fatal, "error", "uncaught", undefined, { err: args[0] });
        options.onFlush?.();
      } catch {
        /* fire-and-forget */
      }
      // Deliberately no swallow: the default behaviour still runs.
    };
    const onRejection = (...args: unknown[]): void => {
      try {
        emit(LEVELS.error, "error", "unhandledrejection", undefined, { err: args[0] });
      } catch {
        /* fire-and-forget */
      }
    };
    const onWarning = (...args: unknown[]): void => {
      try {
        emit(LEVELS.warn, "error", "warning", undefined, { err: args[0] });
      } catch {
        /* fire-and-forget */
      }
    };
    const onExit = (): void => {
      try {
        options.onFlush?.();
      } catch {
        /* fire-and-forget */
      }
    };

    safeVoid(() => proc.on?.("uncaughtException", onUncaught));
    safeVoid(() => proc.on?.("unhandledRejection", onRejection));
    safeVoid(() => proc.on?.("warning", onWarning));
    safeVoid(() => proc.on?.("beforeExit", onExit));
    teardown.push(() => {
      safeVoid(() => proc.off?.("uncaughtException", onUncaught));
      safeVoid(() => proc.off?.("unhandledRejection", onRejection));
      safeVoid(() => proc.off?.("warning", onWarning));
      safeVoid(() => proc.off?.("beforeExit", onExit));
    });
  }

  return () => {
    for (const fn of teardown) safeVoid(fn);
    teardown.length = 0;
  };
}

function safeVoid(fn: () => void): void {
  try {
    fn();
  } catch {
    /* fire-and-forget */
  }
}

function safeCall<T>(fn: () => T, fallback: T): T {
  try {
    return fn();
  } catch {
    return fallback;
  }
}
